import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Eye, EyeOff, Home, User, Building2, AlertCircle, Clock, Gift, Share2, Zap, Users, CheckCircle2, ArrowRight, Sparkles, Bike, Car, UtensilsCrossed, Wrench } from 'lucide-react';
import { waitlistAPI, userAPI } from '../services/api';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import SocialAuthButtons from '../components/SocialAuthButtons';

const SignupPage = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { signup, user: authedUser, getAuthToken, refreshUser } = useAuth();
  const { toast } = useToast();
  // Add-role mode — true when an already-logged-in user lands here from
  // the AddRolePage to expand their account. We skip account creation
  // entirely and call /api/users/me/add-role with the role pulled from
  // the URL. Email/password fields are hidden in this mode.
  const isAddRoleMode = !!authedUser;
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Waitlist states
  const [waitlist, setWaitlist] = useState(false);           // true when Guest gate is OFF
  const [waitlistPhase, setWaitlistPhase] = useState(1);     // 1 = email form, 2 = mini dashboard
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistPhone, setWaitlistPhone] = useState('');
  const [waitlistJoining, setWaitlistJoining] = useState(false);
  const [waitlistData, setWaitlistData] = useState(null);    // returned from API after join
  // Set when the backend returns EMAIL_EXISTS on signup. Triggers a modal
  // that routes the user to the login flow with a `next` param so they
  // land on the add-role page after authenticating.
  const [existingEmailModal, setExistingEmailModal] = useState(null);
  const [step, setStep] = useState(1); // 1: role selection, 2: type selection (if host), 3: form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'guest', // guest, host, delivery_rider, landlord, service_provider (driver/restaurant route to dedicated pages)
    hostType: null, // individual or company (for hosts)
    dateOfBirth: '' // age verification (18+ for all signups)
  });

  // ── Referral code handshake ─────────────────────────────────────
  // Grab ?ref= from URL and persist to localStorage so it survives
  // multi-step sign-up navigation before the form is submitted.
  useEffect(() => {
    const params  = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    if (refCode) {
      localStorage.setItem('bc_ref_code', refCode.trim().toUpperCase());
    }
  }, [location.search]);

  // ── Add-role deep link handshake ────────────────────────────────
  // AddRolePage sends authed users here with ?role=landlord (etc.).
  // Skip the role-selection carousel and jump straight to the form
  // step. For host we still need the company/individual choice.
  // If an authed user lands on /signup with no role param, send them
  // to AddRolePage — the role carousel does not apply to existing
  // accounts and would be confusing.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedRole = params.get('role');
    // guest excluded — you are already a guest by default. Only partner
    // roles need an explicit add-role flow.
    const ROLES = ['host', 'landlord', 'service_provider', 'delivery_rider'];
    if (requestedRole && ROLES.includes(requestedRole)) {
      setFormData(prev => ({ ...prev, role: requestedRole }));
      setStep(requestedRole === 'host' ? 2 : 3);
    } else if (authedUser) {
      navigate('/add-role', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefill name in add-role mode so the user does not have to retype it.
  useEffect(() => {
    if (isAddRoleMode && authedUser?.name && !formData.name) {
      setFormData(prev => ({ ...prev, name: authedUser.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddRoleMode]);

  // Calculate age from DOB
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleRoleSelect = (role) => {
    // Roles with dedicated signup pages that collect specialised fields
    // (vehicle info, FHRS rating, allergen policy, etc.) at signup time.
    if (role === 'driver') {
      navigate('/driver/signup');
      return;
    }
    if (role === 'restaurant_partner') {
      navigate('/restaurant/signup');
      return;
    }

    // All other roles flow through the universal signup form.
    setFormData({ ...formData, role });
    if (role === 'host') {
      setStep(2); // Host type selection (individual / company)
    } else {
      setStep(3); // Universal form (guest, delivery_rider, landlord, service_provider)
    }
  };

  const handleHostTypeSelect = (hostType) => {
    setFormData({ ...formData, hostType });
    if (hostType === 'company') {
      // For company, redirect to company signup flow
      navigate('/signup/company');
    } else {
      setStep(3); // Continue with individual host signup
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Add-role branch: authed user expanding their account. No password
    // checks, no signup call. We just hit /me/add-role and navigate to
    // the role-specific onboarding hint the backend gives us.
    if (isAddRoleMode) {
      setLoading(true);
      try {
        const token = getAuthToken();
        const res = await userAPI.addRole(formData.role, token);
        try { await refreshUser?.(); } catch (_) {}
        toast({
          title: res?.alreadyHadRole ? 'Role already active' : 'Role added',
          description: res?.alreadyHadRole
            ? `Your account already has the ${formData.role.replace('_', ' ')} role.`
            : `${formData.role.replace('_', ' ')} added to your account.`,
        });
        navigate(res?.nextStep || '/', { replace: true });
      } catch (err) {
        const msg = err.response?.data?.detail || 'Could not add role';
        setError(msg);
        toast({ title: 'Failed', description: msg, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    // Age verification for individual hosts
    if (formData.role === 'host' && formData.hostType === 'individual') {
      const age = calculateAge(formData.dateOfBirth);
      if (age === null || age < 18) {
        setError('You must be 18 or older to become a host');
        toast({
          title: 'Age Restriction',
          description: 'You must be 18 or older to become a host',
          variant: 'destructive'
        });
        return;
      }
    }

    setLoading(true);

    try {
      // Read referral code from localStorage (set by ?ref= capture above)
      const referredBy = localStorage.getItem('bc_ref_code') || undefined;
      const result = await signup({
        email:       formData.email,
        password:    formData.password,
        name:        formData.name,
        role:        formData.role,
        dateOfBirth: formData.dateOfBirth,
        ...(referredBy ? { referredBy } : {}),
      });
      // Clear referral code after successful signup — it's been consumed
      localStorage.removeItem('bc_ref_code');

      toast({
        title: 'Welcome to Ariv!',
        description: `Your account has been created.`
      });

      // Redirect based on role
      if (formData.role === 'host') {
        navigate('/host/onboarding');
      } else if (formData.role === 'delivery_rider') {
        navigate('/delivery/dashboard');
      } else if (formData.role === 'landlord') {
        navigate('/landlord/dashboard');
      } else if (formData.role === 'service_provider') {
        navigate('/provider/dashboard');
      } else {
        navigate('/guest/browse');
      }
    } catch (error) {
      console.error('Signup error:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const detailCode = detail && typeof detail === 'object' ? detail.code : null;
      const detailMsg  = typeof detail === 'string' ? detail : (detail?.message || '');

      // Email-already-exists: the backend now returns 409 with
      // { code: "EMAIL_EXISTS" } so we can pop the "this is your
      // account, log in to add this role" modal instead of dead-ending.
      if (status === 409 && detailCode === 'EMAIL_EXISTS') {
        setExistingEmailModal({
          email: formData.email.trim().toLowerCase(),
          role:  formData.role,
        });
        return;
      }

      // Guest gate is closed — show Waitlist screen instead of generic error
      if (detailMsg === 'guestSignupsDisabled' || status === 403) {
        setWaitlist(true);
        return;
      }
      const errorMessage = detailMsg || 'Failed to create account';
      setError(errorMessage);
      toast({
        title: 'Signup Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Waitlist helpers ────────────────────────────────────────────────────
  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail || !waitlistEmail.includes('@')) {
      toast({ title: 'Enter a valid email', variant: 'destructive' });
      return;
    }
    setWaitlistJoining(true);
    try {
      const refCode = localStorage.getItem('bc_ref_code') || null;
      const data = await waitlistAPI.join({
        email:      waitlistEmail.trim().toLowerCase(),
        phone:      waitlistPhone.trim() || null,
        referredBy: refCode,
      });
      setWaitlistData(data);
      setWaitlistPhase(2);
      toast({ title: "You're on the list! 🎟️", description: 'Your referral link is ready.' });
    } catch (err) {
      toast({
        title: 'Could not join waitlist',
        description: err?.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setWaitlistJoining(false);
    }
  };

  const copyWaitlistLink = () => {
    if (!waitlistData?.shareUrl) return;
    navigator.clipboard?.writeText(waitlistData.shareUrl);
    toast({ title: 'Link copied! 🔗', description: 'Share it with Hosts and Drivers.' });
  };

  // ── Phase 1: Premium "Coming Soon" dark card ─────────────────────────────
  const renderWaitlistPhase1 = () => (
    <div className="space-y-0">
      {/* Dark hero banner */}
      <div className="-mx-6 -mt-6 bg-[#0A0F1E] rounded-t-xl px-6 py-8 text-center space-y-3 border-b border-white/10">
        <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-500/30 px-3 py-1 rounded-full mb-1">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-xs font-bold text-amber-400 tracking-wider uppercase">Coming Soon</span>
        </div>
        <h2 className="text-2xl font-black text-white leading-tight">
          Everything arrives.<br />
          <span className="text-amber-400">is almost ready.</span>
        </h2>
        <p className="text-zinc-400 text-sm leading-relaxed max-w-xs mx-auto">
          We're onboarding our top-tier Hosts and Drivers to ensure your first stay is perfect.
          Join the waitlist for <span className="text-emerald-400 font-semibold">0% platform fees on your first 3 bookings.</span>
        </p>
        {/* Social proof row */}
        <div className="flex items-center justify-center gap-4 pt-1">
          {[
            { icon: Users,    text: 'Hosts joining daily' },
            { icon: Zap,      text: 'Launches soon'       },
            { icon: Gift,     text: '0% fee reward'       },
          ].map(({ icon: Icon, text }, i) => (
            <div key={i} className="flex items-center gap-1.5 text-zinc-500 text-xs">
              <Icon size={11} className="text-amber-400" />
              <span>{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Notify Me form */}
      <form onSubmit={handleJoinWaitlist} className="px-1 pt-6 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address</label>
          <input
            type="email"
            required
            autoFocus
            placeholder="you@example.com"
            value={waitlistEmail}
            onChange={e => setWaitlistEmail(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Phone <span className="text-gray-400 font-normal">(optional — for SMS alert)</span>
          </label>
          <input
            type="tel"
            placeholder="+44 7700 000000"
            value={waitlistPhone}
            onChange={e => setWaitlistPhone(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
          />
        </div>
        <button
          type="submit"
          disabled={waitlistJoining}
          className="w-full bg-[#1F3A68] hover:bg-[#162d52] disabled:opacity-60 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
        >
          {waitlistJoining ? (
            <span className="animate-pulse">Joining…</span>
          ) : (
            <>
              <Sparkles size={15} />
              Notify Me &amp; Claim My 0% Fee Reward
              <ArrowRight size={15} />
            </>
          )}
        </button>
        <p className="text-center text-xs text-gray-400">
          No spam. One email when we launch. Unsubscribe anytime.
        </p>
      </form>

      {/* Already a partner / back links */}
      <div className="flex items-center justify-between pt-2 text-xs text-gray-400">
        <Link to="/login" className="text-[#1F3A68] font-semibold hover:underline">
          Already a Partner? Log in
        </Link>
        <button
          type="button"
          onClick={() => setWaitlist(false)}
          className="hover:text-gray-600 underline underline-offset-2"
        >
          Sign up as a Partner
        </button>
      </div>
    </div>
  );

  // ── Phase 2: Mini Referral Dashboard ────────────────────────────────────
  const renderWaitlistPhase2 = () => {
    const pct = waitlistData
      ? Math.min(100, Math.round(((waitlistData.totalWaitlisted || 0) / (waitlistData.launchThreshold || 500)) * 100))
      : 0;
    return (
      <div className="space-y-5">
        {/* Success hero */}
        <div className="-mx-6 -mt-6 bg-[#0A0F1E] rounded-t-xl px-6 py-6 text-center space-y-2 border-b border-white/10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 mx-auto mb-1">
            <CheckCircle2 size={28} className="text-emerald-400" />
          </div>
          <h2 className="text-xl font-black text-white">You're on the list! 🎟️</h2>
          <p className="text-zinc-400 text-sm">
            Spot reserved for <span className="text-emerald-400 font-semibold">{waitlistEmail}</span>
          </p>
        </div>

        {/* Promoted to Waitlist Ambassador */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-amber-600" />
            <span className="text-sm font-bold text-amber-800">You're now a Waitlist Ambassador</span>
          </div>
          <p className="text-xs text-amber-700 leading-relaxed">
            Want to jump the queue and start earning before launch day?{' '}
            <strong>Refer our first 500 Hosts and Drivers now.</strong>{' '}
            You earn a share every time the people you bring on use Ariv — 3% from hosts, 2% from drivers, 1% from delivery riders. Paid for as long as they keep using the app.
          </p>

          {/* Referral code + copy */}
          {waitlistData?.referralCode && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">Your Referral Link</p>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs bg-white border border-amber-300 px-3 py-2 rounded-lg text-amber-900 select-all flex-1 truncate">
                  {waitlistData.shareUrl || `${window.location.origin}/signup?ref=${waitlistData.referralCode}`}
                </span>
                <button
                  type="button"
                  onClick={copyWaitlistLink}
                  className="flex items-center gap-1 text-xs bg-[#1F3A68] text-white px-3 py-2 rounded-lg hover:bg-[#162d52] transition-colors flex-shrink-0"
                >
                  <Share2 size={11} /> Copy
                </button>
              </div>
              <p className="text-[10px] text-amber-600">
                Code: <span className="font-mono font-bold">{waitlistData.referralCode}</span>
              </p>
            </div>
          )}

          {/* Mission goal */}
          <div className="bg-white/60 rounded-lg p-3 border border-amber-200">
            <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1">Your Mission</p>
            <p className="text-xs text-amber-700">
              🎯 Refer <strong>3 Drivers</strong> to secure your <strong>0% Platform Fee for life</strong> — before anyone else gets access.
            </p>
          </div>
        </div>

        {/* Social preview cards */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'WhatsApp',   bg: 'bg-green-500',  emoji: '💬', action: () => window.open(`https://wa.me/?text=${encodeURIComponent(`Join me on Ariv 🏠 — London's new Property Superapp. Use my link to sign up as a Host or Driver: ${waitlistData?.shareUrl || ''}`)}`) },
            { label: 'Copy Link',  bg: 'bg-[#1F3A68]',  emoji: '🔗', action: copyWaitlistLink },
            { label: 'Email',      bg: 'bg-gray-600',   emoji: '📧', action: () => window.open(`mailto:?subject=Join Ariv&body=${encodeURIComponent(`Hey! I'm on the Ariv waitlist — London's new all-in-one property, rides and food app. Sign up as a Host or Driver using my link: ${waitlistData?.shareUrl || ''}`)}`) },
          ].map(({ label, bg, emoji, action }, i) => (
            <button
              key={i}
              type="button"
              onClick={action}
              className={`${bg} text-white text-xs font-semibold py-2.5 rounded-lg flex flex-col items-center gap-1 hover:opacity-90 transition-opacity`}
            >
              <span className="text-lg">{emoji}</span>
              {label}
            </button>
          ))}
        </div>

        {/* Waitlist progress gauge */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-gray-700">Waitlist Progress to Launch</span>
            <span className={`font-bold ${waitlistData?.launchSignal ? 'text-emerald-600' : 'text-amber-600'}`}>
              {waitlistData?.totalWaitlisted?.toLocaleString() || '—'} / {waitlistData?.launchThreshold?.toLocaleString() || '500'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
            <div
              className={`h-2.5 rounded-full transition-all ${waitlistData?.launchSignal ? 'bg-emerald-500' : 'bg-amber-400'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[10px] text-gray-500">
            {waitlistData?.launchSignal
              ? '🚀 Launch threshold reached! Opening soon.'
              : `${500 - (waitlistData?.totalWaitlisted || 0)} more guests needed before we open the doors.`
            }
          </p>
        </div>

        <div className="flex items-center justify-between text-xs text-gray-400">
          <Link to="/login" className="text-[#1F3A68] font-semibold hover:underline">
            Already a Partner? Log in
          </Link>
          <button
            type="button"
            onClick={() => { setWaitlist(false); setWaitlistPhase(1); }}
            className="hover:text-gray-600 underline underline-offset-2"
          >
            Sign up as Partner instead
          </button>
        </div>
      </div>
    );
  };

  // ── Main waitlist renderer (delegates to phase) ──────────────────────────
  const renderWaitlist = () =>
    waitlistPhase === 2 ? renderWaitlistPhase2() : renderWaitlistPhase1();

  // Reusable role-card button (used inside each carousel slide).
  const RoleCard = ({ icon: Icon, label, desc, onClick, testid, fullWidth = false }) => (
    <button
      type="button"
      onClick={onClick}
      className={`p-6 border-2 rounded-xl transition-all hover:border-[#1F3A68]/50 hover:bg-[#1F3A68]/5 ${fullWidth ? 'col-span-2' : ''}`}
      data-testid={testid}
    >
      <Icon className="w-10 h-10 mx-auto mb-3 text-[#1F3A68]" />
      <div className="font-semibold text-lg">{label}</div>
      <div className="text-sm text-gray-600 mt-1">{desc}</div>
    </button>
  );

  // Step 1: Role Selection — all 7 user types shown at once (no swipe), so a
  // new visitor immediately sees every way to join Ariv.
  const renderRoleSelection = () => (
    <div className="space-y-5">
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold text-gray-900">How will you use Ariv?</h2>
        <p className="text-gray-600 mt-1 text-sm">Pick the one that fits you. You can change this later.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <RoleCard
          icon={User}
          label="I'm a Guest"
          desc="Book stays, food, rides & services"
          onClick={() => handleRoleSelect('guest')}
          testid="signup-role-guest"
        />
        <RoleCard
          icon={Home}
          label="I'm a Host"
          desc="List your property"
          onClick={() => handleRoleSelect('host')}
          testid="signup-role-host"
        />
        <RoleCard
          icon={Bike}
          label="I'm a Rider"
          desc="Deliver food on bike or scooter"
          onClick={() => handleRoleSelect('delivery_rider')}
          testid="signup-role-rider"
        />
        <RoleCard
          icon={Car}
          label="I'm a Driver"
          desc="Drive passengers (TfL PHV)"
          onClick={() => handleRoleSelect('driver')}
          testid="signup-role-driver"
        />
        <RoleCard
          icon={Building2}
          label="I'm a Landlord"
          desc="Find an operator for your property"
          onClick={() => handleRoleSelect('landlord')}
          testid="signup-role-landlord"
        />
        <RoleCard
          icon={UtensilsCrossed}
          label="I'm a Restaurant"
          desc="Sell food on Ariv"
          onClick={() => handleRoleSelect('restaurant_partner')}
          testid="signup-role-restaurant"
        />
        <RoleCard
          icon={Wrench}
          label="I'm a Service Provider"
          desc="Cleaner, photographer, tradesperson"
          onClick={() => handleRoleSelect('service_provider')}
          testid="signup-role-service-provider"
          fullWidth
        />
      </div>
    </div>
  );

  // Step 2: Host Type Selection
  const renderHostTypeSelection = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-gray-900">What type of host are you?</h2>
        <p className="text-gray-600 mt-1">This affects your tax and compliance requirements</p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => handleHostTypeSelect('individual')}
          className="p-6 border-2 rounded-xl transition-all hover:border-[#1F3A68]/50 hover:bg-[#1F3A68]/5"
          data-testid="signup-host-individual"
        >
          <User className="w-10 h-10 mx-auto mb-3 text-[#1F3A68]" />
          <div className="font-semibold text-lg">Individual Host</div>
          <div className="text-sm text-gray-600 mt-1">Sole trader / Personal</div>
          <div className="text-xs text-amber-600 mt-2">Must be 18+</div>
        </button>
        
        <button
          type="button"
          onClick={() => handleHostTypeSelect('company')}
          className="p-6 border-2 rounded-xl transition-all hover:border-[#1F3A68]/50 hover:bg-[#1F3A68]/5"
          data-testid="signup-host-company"
        >
          <Building2 className="w-10 h-10 mx-auto mb-3 text-[#1F3A68]" />
          <div className="font-semibold text-lg">Registered Company</div>
          <div className="text-sm text-gray-600 mt-1">Ltd / Business entity</div>
          <div className="text-xs text-gray-500 mt-2">Separate login required</div>
        </button>
      </div>
      
      <Button
        type="button"
        variant="outline"
        onClick={() => setStep(1)}
        className="w-full"
      >
        Back
      </Button>
    </div>
  );

  // Step 3: Registration Form
  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle size={18} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {isAddRoleMode && (
        <div className="p-3 bg-[#1F3A68]/5 border border-[#1F3A68]/20 rounded-lg text-sm text-[#1F3A68]">
          <p className="font-semibold">
            Adding {formData.role.replace('_', ' ')} role to your existing account
          </p>
          <p className="text-xs text-[#1F3A68]/80 mt-0.5 break-all">
            Linked to {authedUser?.email}. Same login, just an extra role.
          </p>
        </div>
      )}

      {!isAddRoleMode && (
        <>
          <div>
            <Label htmlFor="name" className="text-base font-semibold">Full Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="John Doe"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 h-12"
              data-testid="signup-name"
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-base font-semibold">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="mt-1 h-12"
              data-testid="signup-email"
            />
          </div>

          {/* Date of Birth — required for every signup (18+ age gate) */}
          <div>
            <Label htmlFor="dateOfBirth" className="text-base font-semibold">
              Date of Birth <span className="text-red-500">*</span>
            </Label>
            <Input
              id="dateOfBirth"
              type="date"
              required
              value={formData.dateOfBirth}
              onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
              className="mt-1 h-12"
              data-testid="signup-dob"
              max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
            />
            <p className="text-xs text-gray-500 mt-1">You must be 18 or older to use Ariv.</p>
          </div>

          <div>
            <Label htmlFor="password" className="text-base font-semibold">Password</Label>
            <div className="relative mt-1">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="At least 10 characters"
                required
                minLength={10}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="h-12 pr-10"
                data-testid="signup-password"
                aria-describedby="password-strength"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <PasswordStrengthMeter password={formData.password} />
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="text-base font-semibold">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              required
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="mt-1 h-12"
              data-testid="signup-confirm-password"
            />
          </div>

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              required
              className="w-4 h-4 rounded border-gray-300 mt-1"
            />
            <span className="text-sm text-gray-600">
              I agree to the{' '}
              <Link to="/terms" className="text-[#1F3A68] underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-[#1F3A68] underline">Privacy Policy</Link>
            </span>
          </div>
        </>
      )}

      <Button
        type="submit"
        className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90 text-white h-12 text-lg"
        disabled={loading}
        data-testid="signup-submit"
      >
        {loading ? (isAddRoleMode ? 'Adding role…' : 'Creating Account...') : (isAddRoleMode ? 'Add this role' : 'Create Account')}
      </Button>
      
      <Button
        type="button"
        variant="outline"
        onClick={() => setStep(formData.role === 'host' ? 2 : 1)}
        className="w-full"
      >
        Back
      </Button>
    </form>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F2E9] to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img src="/logo.png" alt="Ariv" className="h-[38px] w-auto object-contain mx-auto mb-4" />
          </Link>
          <h1 className="text-3xl font-bold text-[#1F3A68]">Join Ariv</h1>
          <p className="text-gray-600 mt-2">Create your account to get started</p>
        </div>

        <Card className="border-2 border-[#1F3A68]/10 shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-[#1F3A68]">Sign Up</CardTitle>
          </CardHeader>
          <CardContent>
            {waitlist ? renderWaitlist() : (
              <>
                {step === 1 && renderRoleSelection()}
                {step === 2 && renderHostTypeSelection()}
                {step === 3 && renderForm()}
              </>
            )}

            {!waitlist && step === 3 && !isAddRoleMode && (
              <SocialAuthButtons
                mode="signup"
                onSuccess={(result) => {
                  if (result?.requires2FA || result?.requires2FASetup) {
                    navigate('/login?2fa=1');
                    return;
                  }
                  // SSO signups default to the guest role; send them to
                  // the standard guest landing page.
                  navigate('/guest/browse', { replace: true });
                }}
              />
            )}

            {!waitlist && !isAddRoleMode && (
              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#1F3A68] font-semibold hover:underline">
                    Log in
                  </Link>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Duplicate-signup modal — rendered when the backend returns
          EMAIL_EXISTS. We route a "yes, that's me" through the login
          page with a next= param so they land on /add-role afterwards. */}
      {existingEmailModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Existing account"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setExistingEmailModal(null); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#1F3A68] mb-2">
              Looks like you already have an account
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              An account is already registered with{' '}
              <strong className="break-all">{existingEmailModal.email}</strong>.
              Log in to your existing account and we'll take you straight to a page where you can add a new role
              (host, driver, landlord, and more) without creating a duplicate.
            </p>
            <div className="mt-5 grid gap-2">
              <Button
                onClick={() => {
                  const email = encodeURIComponent(existingEmailModal.email);
                  navigate(`/login?next=/add-role&email=${email}`);
                }}
                className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                data-testid="existing-account-login-btn"
              >
                Log in to existing account
              </Button>
              <button
                onClick={() => setExistingEmailModal(null)}
                className="w-full text-sm text-gray-600 hover:text-gray-800 py-2"
              >
                Use a different email
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-4">
              Forgot your password?{' '}
              <Link to="/forgot-password" className="text-[#1F3A68] hover:underline">
                Reset it here
              </Link>
              .
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignupPage;
